<script setup lang="ts">
import { ManualBankAccountType } from '@/types/UserData';

const bankId = defineModel<number | ''>('bankId', { default: '' });
const agency = defineModel<string>('agency', { default: '' });
const accountNumber = defineModel<string>('accountNumber', { default: '' });
const accountType = defineModel<ManualBankAccountType | ''>('accountType', {
  default: '',
});

const typePix = defineModel<string>('typePix', { default: '' });
const keyPix = defineModel<string>('keyPix', { default: '' });

const { fetchBankList } = usePayments();
const { userDocument, userEmail, userPhone, userPhoneDdi } = useUserData();

const typePixOptions = [
  { value: 'CPF', label: 'CPF' },
  { value: 'PHONE', label: 'Telefone' },
  { value: 'EMAIL', label: 'E-mail' },
  { value: 'RANDOM', label: 'Chave Aleatória' },
];

// Só CPF é fixo: a conta precisa estar no CPF do jogador (regra do próprio
// modal), então não tem por que digitar. Telefone/e-mail/aleatória o
// jogador digita livremente — a chave PIX pode ser diferente do
// telefone/e-mail cadastrados no perfil.
const isFreeTextKeyPix = computed(() => typePix.value !== 'CPF');
const isRandomKeyPix = computed(() => typePix.value === 'RANDOM');
const isPhoneKeyPix = computed(() => typePix.value === 'PHONE');
const isEmailKeyPix = computed(() => typePix.value === 'EMAIL');

// "##" consome os dígitos de verdade (inclusive o "55" do DDI, que já vem
// embutido em keyPix) — mask literal ("+", espaços, parênteses) só decora.
// v-maska:[...] lê as opções do argumento, não do valor (ver plugins/maska.client.ts).
const PHONE_MASKA_OPTIONS = {
  mask: ['+## (##) ####-####', '+## (##) #####-####'],
};

watch(typePix, async (type) => {
  await nextTick();

  if (type === 'CPF') {
    keyPix.value = userDocument.value;
  } else if (type === 'EMAIL') {
    keyPix.value = userEmail.value;
  } else if (type === 'PHONE') {
    // userPhoneDdi pode vir com "+" (ex.: "+55") — a chave é só dígitos
    // (ver isValidPhonePixKey/onKeyPixInput), então normaliza aqui também.
    keyPix.value =
      userPhone.value && userPhoneDdi.value
        ? `${userPhoneDdi.value}${userPhone.value}`.replace(/\D/g, '')
        : '';
  } else {
    // RANDOM não pré-preenche: é uma chave arbitrária, sem vínculo com a
    // identidade do jogador — pré-preencher com a que já está cadastrada no
    // perfil arriscaria ele salvar, sem perceber, uma chave que não é dessa
    // conta.
    keyPix.value = '';
  }
});

// Tamanho mínimo de cada chave antes de considerar "o jogador já teve
// chance de terminar de digitar" — evita marcar "inválido" no primeiro
// caractere (ver isValidPhonePixKey/isValidRandomPixKey pros formatos).
const PHONE_KEY_MIN_LENGTH = 12;
const RANDOM_KEY_MIN_LENGTH = 32;

const keyPixError = ref('');

// `strict`: true no blur (valida sempre, mesmo incompleto); false no
// @input (só valida ao vivo depois que a chave já atingiu o tamanho
// esperado, senão fica piscando erro enquanto ele ainda está digitando).
function validateKeyPix(strict = true) {
  if (isRandomKeyPix.value) {
    if (!strict && keyPix.value.length < RANDOM_KEY_MIN_LENGTH) {
      keyPixError.value = '';

      return;
    }

    keyPixError.value = isValidRandomPixKey(keyPix.value)
      ? ''
      : $t('bank_account.manual_modal.key_random_error');
  } else if (isPhoneKeyPix.value) {
    // Com máscara, keyPix.value tem pontuação (ex.: "+55 (19) 99711-9007")
    // — o tamanho que importa é o de dígitos reais.
    const digitCount = keyPix.value.replace(/\D/g, '').length;

    if (!strict && digitCount < PHONE_KEY_MIN_LENGTH) {
      keyPixError.value = '';

      return;
    }

    keyPixError.value = isValidPhonePixKey(keyPix.value)
      ? ''
      : $t('bank_account.manual_modal.key_phone_error');
  } else if (isEmailKeyPix.value) {
    // E-mail não tem tamanho fixo pra "já deu tempo de terminar" — só valida
    // no blur (`strict`), igual agencyError/accountError.
    if (!strict) {
      keyPixError.value = '';

      return;
    }

    keyPixError.value =
      !keyPix.value || isEmailValid(keyPix.value)
        ? ''
        : $t('bank_account.manual_modal.key_email_error');
  } else {
    keyPixError.value = '';
  }
}

type BankOption = { value: number; label: string };

const bankOptions = ref<BankOption[]>([]);
const selectedBank = ref<BankOption | null>(null);
const bankQuery = ref('');
const bankSelectWrapRef = ref<HTMLElement | null>(null);

// Bug real: focar/clicar só o input não abre o painel do HeadlessCombobox
// nessa versão — só abre clicando no HeadlessComboboxButton ou digitando.
// Força o clique real no botão via DOM (evita depender de ref forwarding
// do Headless UI). Só dispara se o clique não foi já no próprio botão
// (senão dispara o toggle nativo dele de novo e fecha o painel que ele
// mesmo acabou de abrir).
function openBankOptionsFromInput(event: MouseEvent) {
  const button = bankSelectWrapRef.value?.querySelector('button');

  if (!button || button.contains(event.target as Node)) return;

  button.click();
}

const typeOptions = [
  {
    value: ManualBankAccountType.CORRENTE,
    label: $t('bank_account.manual_modal.type_corrente'),
  },
  {
    value: ManualBankAccountType.POUPANCA,
    label: $t('bank_account.manual_modal.type_poupanca'),
  },
];

const filteredBankOptions = computed(() => {
  if (!bankQuery.value) return bankOptions.value;

  const query = bankQuery.value.toLowerCase().trim();

  return bankOptions.value.filter((bank) =>
    bank.label.toLowerCase().includes(query),
  );
});

// Começa em `true` (não `false`): o combobox só pode entrar no DOM depois
// que bankOptions já tem dados — ver template (v-if="isBankListLoading").
const isBankListLoading = ref(true);
const isAgencyTouched = ref(false);
const isAccountTouched = ref(false);

// Handoff Tela 5 A11y: "erros por campo com aria-describedby". Só mostra o
// erro depois que o jogador saiu do campo (blur) — não grita "inválido"
// enquanto ele ainda está digitando.
const agencyError = computed(() => {
  if (!isAgencyTouched.value || isValidAgencyOrAccountNumber(agency.value)) {
    return '';
  }

  return $t('bank_account.manual_modal.field_format_error');
});

const accountError = computed(() => {
  if (
    !isAccountTouched.value ||
    isValidAgencyOrAccountNumber(accountNumber.value)
  ) {
    return '';
  }

  return $t('bank_account.manual_modal.field_format_error');
});

// Mantém dígitos e um hífen opcional pro dígito verificador (ex.: "12345-6"
// ou "12345-X") — o dígito principal (antes do hífen) é sempre só números;
// o "X" só é válido como DV (Módulo 11, resto 10 — comum no Banco do
// Brasil, tanto na agência quanto na conta). Separado em
// `agency_digit`/`number_digit` no envio (ver ManualAccountModal).
//
// "X" resolve na hora (sem esperar o blur): diferente de um dígito comum,
// ele nunca faz parte do número principal — só pode ser o DV. Então, assim
// que o jogador digita o X, já insere o hífen antes dele. Isso evita o erro
// de formato piscando enquanto o campo ainda tem foco (ver formatDvOnBlur
// pro caso do DV numérico, que é ambíguo até o blur).
function sanitizeDigits(field: 'agency' | 'accountNumber', event: Event) {
  const input = event.target as HTMLInputElement;
  let value = input.value.toUpperCase().replace(/[^\dX-]/g, '');

  if (value.endsWith('X') && value.length > 1 && value.slice(-2, -1) !== '-') {
    value = `${value.slice(0, -1)}-X`;
  }

  // Bug real: quando o valor sanitizado é igual ao que já estava no ref
  // (ex.: campo vazio + caractere inválido = "" antes e depois), o Vue não
  // dispara re-render (sem mudança real no ref, a reatividade pula) — o
  // :value="agency"/:value="accountNumber" nunca repatcha o DOM, e o
  // caractere que o navegador já inseriu nativamente na tecla fica visível
  // na tela como se tivesse sido aceito, mesmo o model por baixo estando
  // certo. Corrige o input diretamente, sem depender do Vue re-renderizar.
  input.value = value;

  if (field === 'agency') agency.value = value;
  else accountNumber.value = value;
}

// Só EMAIL/RANDOM chegam aqui — telefone tem input próprio com máscara
// (v-maska cuida da formatação, keyPix já vem com os dígitos certos).
function onKeyPixInput(event: Event) {
  keyPix.value = (event.target as HTMLInputElement).value;
  validateKeyPix(false);
}

// Handoff Tela 5 (placeholder "00000-0"/"0000-0"): se o jogador digitou só
// dígitos, sem hífen, separa o dígito verificador ao sair do campo — sem
// isso o número inteiro vira `agency`/`number` e o `_digit` chega vazio no
// payload (ver ManualAccountModal.saveAccount). Mesma regra pros dois
// campos — o "X" já foi resolvido em tempo real por sanitizeDigits, isso
// só cobre o DV numérico, que só dá pra separar quando o jogador termina
// de digitar (não dá pra saber antes do blur).
function formatDvOnBlur(field: 'agency' | 'accountNumber') {
  const target = field === 'agency' ? agency : accountNumber;

  if (field === 'agency') isAgencyTouched.value = true;
  else isAccountTouched.value = true;

  const value = target.value;

  if (value.includes('-') || value.length < 2) return;

  target.value = `${value.slice(0, -1)}-${value.slice(-1)}`;
}

// Edição: se o campo já vem preenchido (conta existente), pré-seleciona o
// banco correspondente assim que a lista carregar.
onMounted(async () => {
  isBankListLoading.value = true;

  const banks = await fetchBankList();

  bankOptions.value = (banks?.data ?? []).map((bank: Bank) => ({
    value: bank.id,
    label: bank.name,
  }));

  isBankListLoading.value = false;

  if (bankId.value) {
    selectedBank.value =
      bankOptions.value.find((bank) => bank.value === bankId.value) ?? null;
  }
});

watch(selectedBank, (bank) => {
  bankId.value = bank?.value ?? '';
});
</script>

<template>
  <span :class="$style.fieldLabel">
    {{ $t('bank_account.manual_modal.bank_label') }}
  </span>
  <div
    v-if="isBankListLoading"
    :class="[$style.selectTrigger, $style.loadingPlaceholder]"
  >
    {{ $t('bank_account.manual_modal.bank_loading') }}
  </div>
  <!-- Headless UI (bug real, não hipotético): monta com a lista vazia e só
       reconcilia os <HeadlessComboboxOption> quando algo dispara @change
       (digitar) — clicar sem digitar não força essa atualização. Por isso
       o combobox só entra no DOM depois que bankOptions já tem os bancos. -->
  <HeadlessCombobox
    v-else
    v-model="selectedBank"
    nullable
    @update:model-value="bankQuery = ''"
  >
    <div
      ref="bankSelectWrapRef"
      :class="$style.selectWrap"
    >
      <HeadlessComboboxInput
        :class="[$style.selectTrigger, $style.comboboxInput]"
        :display-value="
          (bank: unknown) => (bank as BankOption | null)?.label ?? ''
        "
        :placeholder="$t('bank_account.manual_modal.bank_placeholder')"
        @change="bankQuery = $event.target.value"
        @click="openBankOptionsFromInput"
      />
      <HeadlessComboboxButton :class="$style.chevronButton">
        <BankChevronIcon :class="$style.chevron" />
      </HeadlessComboboxButton>

      <HeadlessComboboxOptions :class="$style.optionsList">
        <li
          v-if="!filteredBankOptions.length"
          :class="$style.notFound"
        >
          {{ $t('bank_account.manual_modal.bank_not_found') }}
        </li>
        <HeadlessComboboxOption
          v-for="bank in filteredBankOptions"
          :key="bank.value"
          v-slot="{ active }"
          as="template"
          :value="bank"
        >
          <li :class="[$style.option, { [$style.optionActive]: active }]">
            {{ bank.label }}
          </li>
        </HeadlessComboboxOption>
      </HeadlessComboboxOptions>
    </div>
  </HeadlessCombobox>

  <label
    :class="$style.fieldLabel"
    for="bank-agency"
  >
    {{ $t('bank_account.manual_modal.agency_label') }}
  </label>
  <input
    id="bank-agency"
    :aria-describedby="agencyError ? 'bank-agency-error' : undefined"
    :aria-invalid="!!agencyError"
    :class="$style.textInput"
    inputmode="numeric"
    maxlength="6"
    :placeholder="$t('bank_account.manual_modal.agency_placeholder')"
    :value="agency"
    @blur="formatDvOnBlur('agency')"
    @input="sanitizeDigits('agency', $event)"
  />
  <span
    v-if="agencyError"
    id="bank-agency-error"
    :class="$style.fieldError"
    role="alert"
  >
    {{ agencyError }}
  </span>

  <label
    :class="$style.fieldLabel"
    for="bank-account"
  >
    {{ $t('bank_account.manual_modal.account_label') }}
  </label>
  <input
    id="bank-account"
    :aria-describedby="accountError ? 'bank-account-error' : undefined"
    :aria-invalid="!!accountError"
    :class="$style.textInput"
    inputmode="numeric"
    maxlength="14"
    :placeholder="$t('bank_account.manual_modal.account_placeholder')"
    :value="accountNumber"
    @blur="formatDvOnBlur('accountNumber')"
    @input="sanitizeDigits('accountNumber', $event)"
  />
  <span
    v-if="accountError"
    id="bank-account-error"
    :class="$style.fieldError"
    role="alert"
  >
    {{ accountError }}
  </span>

  <span :class="$style.fieldLabel">
    {{ $t('bank_account.manual_modal.type_label') }}
  </span>
  <div :class="$style.selectWrap">
    <select
      v-model="accountType"
      :class="[$style.selectTrigger, { [$style.hasValue]: !!accountType }]"
    >
      <option
        disabled
        value=""
      >
        {{ $t('bank_account.manual_modal.type_placeholder') }}
      </option>
      <option
        v-for="type in typeOptions"
        :key="type.value"
        :value="type.value"
      >
        {{ type.label }}
      </option>
    </select>
    <BankChevronIcon :class="$style.chevronStandalone" />
  </div>

  <span :class="$style.fieldLabel">
    {{ $t('bank_account.manual_modal.key_type_label') }}
  </span>
  <div :class="$style.selectWrap">
    <select
      v-model="typePix"
      :class="[$style.selectTrigger, { [$style.hasValue]: !!typePix }]"
    >
      <option
        disabled
        value=""
      >
        {{ $t('bank_account.manual_modal.key_type_placeholder') }}
      </option>
      <option
        v-for="option in typePixOptions"
        :key="option.value"
        :value="option.value"
      >
        {{ option.label }}
      </option>
    </select>
    <BankChevronIcon :class="$style.chevronStandalone" />
  </div>

  <label
    :class="$style.fieldLabel"
    for="bank-key-pix"
  >
    {{ $t('bank_account.manual_modal.key_label') }}
  </label>
  <input
    v-if="isPhoneKeyPix"
    id="bank-key-pix"
    v-model="keyPix"
    v-maska:[PHONE_MASKA_OPTIONS]
    :aria-describedby="keyPixError ? 'bank-key-pix-error' : undefined"
    :aria-invalid="!!keyPixError"
    :class="$style.textInput"
    inputmode="tel"
    :placeholder="$t('bank_account.manual_modal.key_free_placeholder')"
    @blur="validateKeyPix()"
    @input="validateKeyPix(false)"
  />
  <input
    v-else-if="isFreeTextKeyPix"
    id="bank-key-pix"
    :aria-describedby="keyPixError ? 'bank-key-pix-error' : undefined"
    :aria-invalid="!!keyPixError"
    :class="$style.textInput"
    :placeholder="
      isRandomKeyPix
        ? $t('bank_account.manual_modal.key_random_placeholder')
        : $t('bank_account.manual_modal.key_free_placeholder')
    "
    :value="keyPix"
    @blur="validateKeyPix()"
    @input="onKeyPixInput"
  />
  <!-- CPF é fixo (regra do modal: a conta tem que estar no CPF do jogador,
       ver isFreeTextKeyPix acima) — não editável, não precisa de <select>. -->
  <input
    v-else
    id="bank-key-pix"
    :class="[$style.textInput, $style.readonlyInput]"
    readonly
    :value="cpfMask(keyPix)"
  />
  <span
    v-if="keyPixError"
    id="bank-key-pix-error"
    :class="$style.fieldError"
    role="alert"
  >
    {{ keyPixError }}
  </span>
</template>

<style module lang="postcss">
.fieldLabel {
  @apply block text-texts/70 text-xs font-semibold mt-4 mb-1.5;
}

.selectWrap {
  @apply relative;
}

.selectTrigger,
.textInput {
  @apply w-full h-12 px-4 rounded-lg border border-solid border-transparent bg-auth-bg-inputs text-[15px] outline-none;
}

.selectTrigger {
  @apply appearance-none text-texts/40 cursor-pointer pr-9;

  /* Bug real: `appearance-none` do Tailwind sozinho não basta em alguns
     WebKit móveis pra remover a seta nativa do <select> — sobra a seta do
     navegador por trás do nosso ícone customizado ("duas setas"). */
  -webkit-appearance: none;
  -moz-appearance: none;

  &.hasValue {
    @apply text-texts;
  }
}

.loadingPlaceholder {
  @apply flex items-center text-texts/40 cursor-default;
}

.textInput {
  @apply text-texts;

  &[aria-invalid='true'] {
    @apply border-error;
  }
}

.readonlyInput {
  @apply text-texts/40 cursor-default;
}

.fieldError {
  @apply block mt-1 text-xs text-error;
}

.chevron {
  @apply pointer-events-none stroke-texts;
}

/* Mesmo mecanismo de centralização do .chevronButton (flexbox, não
   transform) — só assim as duas setas ficam pixel-a-pixel iguais. */
.chevronStandalone {
  @apply pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4 stroke-texts;
}

/* Bug real: com `display: contents` (sem caixa própria) + ícone com
   pointer-events-none, o botão nunca recebia clique — passava direto pro
   elemento por trás. Precisa de área clicável de verdade, igual ao padrão
   já usado em components/base/form/Combobox.vue (.arrowButton). */
.chevronButton {
  @apply absolute inset-y-0 right-0 flex items-center pr-4;
}

.comboboxInput {
  @apply !text-texts placeholder:text-texts/40;
}

.optionsList {
  @apply absolute z-10 mt-1 w-full max-h-48 overflow-auto rounded-lg bg-auth-bg-inputs py-1 text-[15px] shadow-lg ring-1 ring-white/10;
}

.notFound {
  @apply px-4 py-2 text-sm text-texts/50;
}

.option {
  @apply px-4 py-2 text-sm text-texts cursor-pointer select-none;
}

.optionActive {
  @apply bg-primary/20;
}
</style>
